import {
  Divider,
  List,
  ListSubheader,
  Skeleton,
  Tab,
  Tabs,
} from '@mui/material';
import {
  useGetCategoriesQuery,
  useGetItemsByCategoryQuery,
} from 'api/public/b2b';
import { B2BItem } from 'api/public/types';
import { messages } from 'features/main/messages';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import EmptyState from 'shared/components/EmptyState/EmptyState';
import LinkWithQuery from 'shared/components/LinkWithQuery/LinkWithQuery';
import SkeletonCard from 'shared/components/ListProgress/components/SkeletonCard';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import PageNotFound from 'shared/components/PageNotFound/PageNotFound';
import { PAGE_INDEX, PAGE_SIZE } from 'shared/constants/common';
import { logEvent } from 'shared/utils/gtm';
import { useMyLocation } from '../Location/hooks/useMyLocation';
import CategoryListItem from './components/CategoryListItem/CategoryListItem';

const Categories = () => {
  const { t } = useTranslation();
  const { categorySlug = '' } = useParams();
  const myLocation = useMyLocation();

  const { data: categories, isFetching: isFetchingCategories } =
    useGetCategoriesQuery();

  const selectedCategory = categories?.find((c) => c.slug === categorySlug);

  const [dataPageIndex, setDataPageIndex] = useState<number>(PAGE_INDEX);
  const [dataItems, setDataItems] = useState<B2BItem[]>([]);

  const shouldResetPageIndex = !!dataItems.some(
    (v) => v.category[0] !== categorySlug
  );

  useEffect(() => {
    if (selectedCategory?.title_vi_vn) {
      logEvent('Screen_Category_details', {
        category: selectedCategory?.title_vi_vn,
      });
    }
  }, []);

  useEffect(() => {
    logEvent('Category_detail_switch_tab', {
      category: selectedCategory?.title_vi_vn,
    });

    if (shouldResetPageIndex) {
      setDataPageIndex(0);
    }
  }, [categorySlug]);

  // const { data: items, isFetching: isFetchingItems } = useItemsByCategory();
  const { data: items, isFetching: isFetchingItems } =
    useGetItemsByCategoryQuery(
      {
        slug: selectedCategory?.slug as string,
        lat: myLocation?.lat,
        lng: myLocation?.lng,
        page: shouldResetPageIndex ? 0 : dataPageIndex,
      },
      {
        skip: !selectedCategory,
        // refetchOnMountOrArgChange: true,
      }
    );

  useEffect(() => {
    if (items) {
      if (shouldResetPageIndex) {
        setDataItems(items);
      } else {
        setDataItems((value) => [...value, ...items]);
      }
    }
  }, [items]);

  if (!selectedCategory && !isFetchingCategories) {
    return <PageNotFound />;
  }

  return (
    <>
      <>
        <title>
          {selectedCategory?.title_vi_vn ?? t(messages.categoriesTitle())}
        </title>
        <meta
          name='description'
          content={
            selectedCategory?.title_vi_vn ?? t(messages.categoriesTitle())
          }
        />
      </>
      <PageHeader
        title={selectedCategory?.title_vi_vn ?? t(messages.categoriesTitle())}
        backLink={'/'}
      />
      <InfiniteScroll
        scrollThreshold={0.75}
        scrollableTarget={isMobile ? 'body' : 'scrollableTarget'}
        dataLength={(dataItems && dataItems.length) ?? 0} //This is important field to render the next data
        next={() => setDataPageIndex((o) => o + 1)}
        hasMore={!!(items?.length === PAGE_SIZE)}
        loader={
          <List>
            {Array.from(Array(3).keys()).map((item) => (
              <li key={item}>
                <Divider />
                <SkeletonCard />
              </li>
            ))}
          </List>
        }
        // endMessage={
        //   <p style={{ textAlign: 'center' }}>
        //     <b>Yay! You have seen it all</b>
        //   </p>
        // }
      >
        <List className='divide-y divide-[#f3f4f8]'>
          <>
            {/* <SearchBlock /> */}

            {isFetchingCategories ? (
              <ListSubheader>
                <Skeleton animation='wave' />
              </ListSubheader>
            ) : (
              <ListSubheader disableGutters>
                <Tabs
                  className='shadow-md'
                  value={selectedCategory?.slug}
                  variant='scrollable'
                  scrollButtons={true}
                  aria-label='scrollable prevent tabs example'
                  TabIndicatorProps={{ className: '!bg-[#121619]' }}
                >
                  {categories?.map((item, idx) => (
                    <Tab
                      sx={{
                        minWidth: 'auto',
                      }}
                      key={idx}
                      label={item.title_vi_vn}
                      component={LinkWithQuery}
                      value={item.slug}
                      to={`/${item.slug}`}
                    />
                  ))}
                </Tabs>
              </ListSubheader>
            )}
          </>
          <Divider component='li' sx={{ borderTopWidth: '12px !important' }} />
          {dataItems && dataItems.length > 0 ? (
            dataItems.map((item) => (
              <CategoryListItem
                key={item._id}
                item={item}
                onClick={() =>
                  logEvent('Category_detail_click_Item_detail', {
                    category: selectedCategory?.title_vi_vn,
                    item_name: item.title_vi_vn,
                  })
                }
              />
            ))
          ) : isFetchingItems || isFetchingCategories ? (
            <>
              {Array.from(Array(3).keys()).map((item) => (
                <li key={item}>
                  <SkeletonCard />
                </li>
              ))}
            </>
          ) : (
            <EmptyState title='Trong này chưa có mặt hàng nào. Vui lòng quay lại sau bạn nhé!' />
          )}
        </List>
      </InfiniteScroll>
    </>
  );
};

export default Categories;
